import React, { useEffect, useRef } from 'react';
import { LockIcon, XIcon } from 'lucide-react';
import { Patient } from '../../types';
type PatientRegistrationDetailsProps = {
  patient: Patient;
  onClose: () => void;
};
type Field = {
  label: string;
  value: string | number | null | undefined;
};

/**
 * Read-only view of every field captured on the Register New Patient form,
 * read from the saved patient record. Blank fields show "Not Provided".
 */
export function PatientRegistrationDetails({
  patient,
  onClose
}: PatientRegistrationDetailsProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  const registered = patient.registeredAt ? new Date(patient.registeredAt) : null;
  const registeredLabel = registered && !Number.isNaN(registered.getTime()) ? registered.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }) : null;
  const groups: {
    title: string;
    fields: Field[];
  }[] = [{
    title: 'Personal information',
    fields: [{
      label: 'First name',
      value: patient.firstName
    }, {
      label: 'Last name',
      value: patient.lastName
    }, {
      label: 'Sex',
      value: patient.sex
    }, {
      label: 'Age',
      value: patient.age ? `${patient.age} years` : null
    }, {
      label: 'Marital status',
      value: patient.maritalStatus
    }, {
      label: 'Occupation',
      value: patient.occupation
    }]
  }, {
    title: 'Contact details',
    fields: [{
      label: 'Contact number',
      value: patient.contact
    }, {
      label: 'Address',
      value: patient.address
    }]
  }, {
    title: 'Next of kin',
    fields: [{
      label: 'Name',
      value: patient.nextOfKin
    }, {
      label: 'Contact number',
      value: patient.nokContact
    }, {
      label: 'Relationship',
      value: patient.nokRelationship
    }]
  }, {
    title: 'Recorded at registration',
    fields: [{
      label: 'Allergies',
      value: patient.allergies
    }, {
      label: 'HIV status',
      value: patient.hivStatus
    }]
  }];
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4" onMouseDown={event => {
    if (event.target === event.currentTarget) onClose();
  }} data-id="element-9915" data-mp="dod8xniymdtp">
      <div role="dialog" aria-modal="true" aria-labelledby="patient-details-title" className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl" data-id="element-9916" data-mp="nezh4wock22d">
        <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4 sm:px-6" data-id="element-9917" data-mp="wfxkdcqyo5jw">
          <div className="min-w-0" data-id="element-9918" data-mp="ri4g4p97xlm9">
            <p className="font-mono text-sm font-semibold text-blue-700" data-id="element-9919" data-mp="1b2ct3oscg7jj">
              {patient.bmcFileNumber || 'No patient number'}
            </p>
            <h2 id="patient-details-title" className="mt-0.5 truncate text-xl font-bold text-gray-900" data-id="element-9920" data-mp="1g0o3q1qgqm2t">
              {[patient.firstName, patient.lastName].filter(Boolean).join(' ') || 'Unnamed patient'}
            </h2>
            <p className="mt-1 text-sm text-gray-500" data-id="element-9921" data-mp="sak8ahnfhsmw">
              Registered {registeredLabel ?? 'date not recorded'}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2" data-id="element-9922" data-mp="x44wxj34ta9f">
            <span className="hidden items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 sm:inline-flex" data-id="element-9923" data-mp="if0fw582pc2w">
              <LockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-9924" data-mp="9cr4ze8w7goz" />
              View only
            </span>
            <button ref={closeRef} type="button" onClick={onClose} aria-label="Close patient details" className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-chrome-active" data-id="element-9925" data-mp="ebkj98w07bq7">
              <XIcon className="h-5 w-5" data-id="element-9926" data-mp="3sq7dle545q9y" />
            </button>
          </div>
        </header>

        <div className="space-y-6 overflow-y-auto px-5 py-5 sm:px-6" data-id="element-9927" data-mp="3opnjkjfu72s1">
          {groups.map(group => <section key={group.title} aria-labelledby={`pd-${group.title}`} data-id="element-9928" data-mp="3jqaxi0wshzqi">
              <h3 id={`pd-${group.title}`} className="mb-2 border-b border-gray-100 pb-1.5 text-sm font-semibold text-gray-900" data-id="element-9929" data-mp="3er7qwc15vlgf">
                {group.title}
              </h3>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2" data-id="element-9930" data-mp="35r1jn7w23hpp">
                {group.fields.map(field => {
              const text = field.value === null || field.value === undefined || String(field.value).trim() === '' ? null : String(field.value);
              return <div key={field.label} data-id="element-9931" data-mp="2qukye6jqmfhx">
                      <dt className="text-xs text-gray-500" data-id="element-9932" data-mp="2qt9rvyywqtn4">{field.label}</dt>
                      <dd className={text ? 'whitespace-pre-line break-words text-sm font-medium text-gray-900' : 'text-sm italic text-gray-400'} data-id="element-9933" data-mp="2vn4lbu4bemze">
                        {text ?? 'Not Provided'}
                      </dd>
                    </div>;
            })}
              </dl>
            </section>)}
        </div>

        <footer className="flex justify-end border-t border-gray-200 px-5 py-3 sm:px-6" data-id="element-9934" data-mp="2gyq9wusu1q78">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-chrome-active" data-id="element-9935" data-mp="3ncz8pl8m42lf">
            Close
          </button>
        </footer>
      </div>
    </div>;
}